import {IconBoltFilled} from "@tabler/icons-react";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {
  GAMANZA_PLAYER_DRAWER_CLOSE_EVENT,
  GAMANZA_PLAYER_DRAWER_OPEN_EVENT,
} from "@/components/GamanzaPlayerDrawer/gamanzaPlayerDrawerEvents";
import useDraggableButtonPosition from "@/hooks/useDraggableButtonPosition";
import useGamanzaWidgetHydration from "@/hooks/useGamanzaWidgetHydration";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import {useIsGamanzaUiEnabled} from "@/utils/gamanzaUi";
import {useGetTopGames} from "../../hooks/gamesList/useGetTopGames";
import {useAuthStore} from "../../store/useAuth.store";
import {useBingoStore} from "../../store/useBingo.store";
import {useHorsesStore} from "../../store/useHorses.store";
import {useMiniGames} from "../../store/useMiniGames";
import MiniGameModal from "./MiniGameModal";
import styles from "./MultiGameFloatingButton.module.css";
import MultiGameModal from "./MultiGameModal";
import ParallelGameModal from "./ParallelGameModal";

const FAB_AVATAR_WIDGET_ID = "gamanza-quick-games-fab-avatar";
/** Docs only allow 50 / 110 / 140 — scale via CSS to FAB size */
const GAMANZA_AVATAR_DATA_SIZE = 50;

const MultiGameFloatingButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMiniModalOpen, setIsMiniModalOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const buttonRef = React.useRef(null);
  const avatarHostRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [buttonPosition, setButtonPosition] = useDraggableButtonPosition();
  const hasDraggedRef = React.useRef(false);
  const dragStartPos = React.useRef({x: 0, y: 0});
  const dragOffsetRef = React.useRef({x: 0, y: 0});
  const pointerDragCleanupRef = React.useRef(null);

  const [parallelGame, setParallelGame] = useState(null);
  const [isParallelGameMinimized, setIsParallelGameMinimized] = useState(false);
  const [isDocumentFullscreen, setIsDocumentFullscreen] = useState(false);
  const [isGamanzaDrawerOpen, setIsGamanzaDrawerOpen] = useState(false);

  const {t} = useTranslation();
  const authStatus = useAuthStore(state => state.authStatus);
  const token = useAuthStore(state => state.token);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const gamanzaUiEnabled = useIsGamanzaUiEnabled();
  const showGamanzaAvatar = gamanzaUiEnabled && authStatus === "authenticated" && Boolean(token);
  const {isHydrating: isAvatarHydrating} = useGamanzaWidgetHydration({
    enabled: showGamanzaAvatar && !isMiniModalOpen && !isModalOpen && !isGamanzaDrawerOpen,
    token,
    rootRef: avatarHostRef,
    widgetId: FAB_AVATAR_WIDGET_ID,
  });
  const {data: topGamesData} = useGetTopGames();
  const initializeGames = useGameMetadataStore(state => state.initializeGames);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const gamesLoading = useGameMetadataStore(state => state.loading);
  const gamesInitialized = useGameMetadataStore(state => state.initialized);
  const allGames = useGameMetadataStore(state => state.allGames);
  const screenMode = useMiniGames(state => state.screenMode);
  const horsesScreenMode = useHorsesStore(state => state.screenMode);
  const bingoScreenMode = useBingoStore(state => state.screenMode);
  const location = useLocation();

  useEffect(() => {
    const onOpen = () => setIsGamanzaDrawerOpen(true);
    const onClose = () => setIsGamanzaDrawerOpen(false);
    window.addEventListener(GAMANZA_PLAYER_DRAWER_OPEN_EVENT, onOpen);
    window.addEventListener(GAMANZA_PLAYER_DRAWER_CLOSE_EVENT, onClose);
    return () => {
      window.removeEventListener(GAMANZA_PLAYER_DRAWER_OPEN_EVENT, onOpen);
      window.removeEventListener(GAMANZA_PLAYER_DRAWER_CLOSE_EVENT, onClose);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement ||
        (window.innerHeight === screen.height && window.innerWidth === screen.width)
      );
      setIsDocumentFullscreen(isFullscreen);
    };

    const handleKeyPress = event => {
      if (event.key === "m" || event.key === "M") {
        if (isDocumentFullscreen) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
          setTimeout(() => {
            setIsModalOpen(true);
          }, 300);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);
    window.addEventListener("resize", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyPress);

    handleFullscreenChange();

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
      window.removeEventListener("resize", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isDocumentFullscreen]);

  useEffect(() => {
    if (!gamesInitialized && !gamesLoading) {
      initializeGames();
    }
  }, [gamesInitialized, gamesLoading, initializeGames]);

  const topSixGames = React.useMemo(() => {
    const listTopGames = topGamesData?.data?.data || [];

    if (!gamesInitialized || !allGames || allGames.length === 0) {
      return [];
    }

    const processedGames = listTopGames.reduce((acc, topGame) => {
      const game = getGameById(topGame.game_id, topGame.game_provider);

      if (game) {
        acc.push(game);
      } else {
        acc.push({
          id: topGame.game_id,
          provider: topGame.game_provider,
          name: topGame.game_name || topGame.name || topGame.game_id,
          game_name: topGame.game_name || topGame.name || topGame.game_id,
          icon: topGame.icon || topGame.game_icon || topGame.image || null,
          isFav: false,
          enable: true,
        });
      }
      return acc;
    }, []);

    return processedGames.slice(0, 6);
  }, [topGamesData, allGames, gamesInitialized, getGameById]);

  useEffect(() => {
    const handleOtherMenuOpen = () => {
      if (isModalOpen) {
        setIsModalOpen(false);
      }
      if (isMiniModalOpen) {
        setIsMiniModalOpen(false);
      }
    };

    window.addEventListener("floating-menu-opened", handleOtherMenuOpen);
    window.addEventListener("wallet-menu-opened", handleOtherMenuOpen);

    return () => {
      window.removeEventListener("floating-menu-opened", handleOtherMenuOpen);
      window.removeEventListener("wallet-menu-opened", handleOtherMenuOpen);
    };
  }, [isModalOpen, isMiniModalOpen]);

  useEffect(() => {
    if (!hasInteracted) {
      setIsModalOpen(false);
      setIsMiniModalOpen(false);
    }
  }, [authStatus, hasInteracted]);

  const handleButtonClick = () => {
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }

    if (authStatus !== "authenticated") {
      useAuthStore.getState().handleAuthForm("login");
      handleShowAuthModal();
      return;
    }

    setHasInteracted(true);
    window.dispatchEvent(new CustomEvent("multi-game-menu-opened"));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsMiniModalOpen(false);
  };

  const handleMinimizeModal = () => {
    setIsModalOpen(false);
    setIsMiniModalOpen(true);
  };

  const handleCloseMiniModal = () => {
    setIsMiniModalOpen(false);
  };

  const handleOpenFullModal = () => {
    setIsMiniModalOpen(false);
    setIsModalOpen(true);
  };

  const handleGameSelect = selectedGame => {
    setIsModalOpen(false);
    setIsMiniModalOpen(false);
    setParallelGame(selectedGame);
    setIsParallelGameMinimized(false);
  };

  const handleCloseParallelGame = () => {
    setParallelGame(null);
    setIsParallelGameMinimized(false);
  };

  const handleMinimizeParallelGame = () => {
    setIsParallelGameMinimized(!isParallelGameMinimized);
  };

  const updatePositionFromPointer = (clientX, clientY) => {
    const buttonSize = 70;
    const bottomNavHeight = 90;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxY = viewportHeight - buttonSize - bottomNavHeight;

    let newX = clientX - dragOffsetRef.current.x;
    let newY = clientY - dragOffsetRef.current.y;

    if (newX < 0) {
      newX = 0;
      dragOffsetRef.current = {...dragOffsetRef.current, x: clientX};
    } else if (newX > viewportWidth - buttonSize) {
      newX = viewportWidth - buttonSize;
      dragOffsetRef.current = {...dragOffsetRef.current, x: clientX - newX};
    }

    if (newY < 0) {
      newY = 0;
      dragOffsetRef.current = {...dragOffsetRef.current, y: clientY};
    } else if (newY > maxY) {
      newY = maxY;
      dragOffsetRef.current = {...dragOffsetRef.current, y: clientY - newY};
    }

    setButtonPosition({x: newX, y: newY});

    const dx = clientX - dragStartPos.current.x;
    const dy = clientY - dragStartPos.current.y;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasDraggedRef.current = true;
    }
  };

  const handlePointerDown = e => {
    if (e.pointerType === "mouse" && e.button !== 0) return;

    e.currentTarget.setPointerCapture(e.pointerId);

    hasDraggedRef.current = false;
    dragStartPos.current = {x: e.clientX, y: e.clientY};
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);

    pointerDragCleanupRef.current?.();

    const pointerId = e.pointerId;

    const onPointerMove = moveEvent => {
      if (moveEvent.pointerId !== pointerId) return;
      moveEvent.preventDefault();
      updatePositionFromPointer(moveEvent.clientX, moveEvent.clientY);
    };

    const onPointerUp = upEvent => {
      if (upEvent.pointerId !== pointerId) return;
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
      pointerDragCleanupRef.current = null;
      setIsDragging(false);
    };

    pointerDragCleanupRef.current = () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };

    document.addEventListener("pointermove", onPointerMove, {passive: false});
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
  };

  useEffect(() => {
    return () => {
      pointerDragCleanupRef.current?.();
      pointerDragCleanupRef.current = null;
    };
  }, []);

  const isOverlayFullscreen = useMemo(() => {
    const gamesFs = screenMode === "fullscreen";
    const horsesFs = horsesScreenMode === "fullscreen";
    const bingoFs = bingoScreenMode === "fullscreen";
    return Boolean(gamesFs || horsesFs || bingoFs);
  }, [screenMode, horsesScreenMode, bingoScreenMode]);

  const isInAnyGameScreen = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith("/games/")) {
      const parts = p.split("/").filter(Boolean);
      return parts.length >= 3;
    }
    if (p.startsWith("/horses")) return true;
    if (p.startsWith("/bingo")) return true;
    return false;
  }, [location.pathname]);

  const buttonStyle = useMemo(() => {
    const baseStyle = {
      padding: 0,
      border: "none",
      borderRadius: "50%",
      cursor: isDragging ? "grabbing" : "grab",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "56px",
      height: "56px",
      transition: isDragging ? "none" : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    };

    if (buttonPosition) {
      return {
        ...baseStyle,
        position: "fixed",
        top: `${buttonPosition.y}px`,
        left: `${buttonPosition.x}px`,
        zIndex: 99999,
      };
    }

    if (isDocumentFullscreen) {
      return {
        ...baseStyle,
        position: "fixed",
        top: "60%",
        right: "20px",
        zIndex: 99999,
        pointerEvents: "auto",
        visibility: "visible",
        opacity: "1",
        display: "flex",
        isolation: "isolate",
        willChange: "transform",
      };
    } else if (isInAnyGameScreen && isOverlayFullscreen) {
      return {
        ...baseStyle,
        position: "fixed",
        top: "60%",
        right: "20px",
        zIndex: 99999,
      };
    } else {
      return {
        ...baseStyle,
        position: "fixed",
        bottom: "100px",
        right: "15px",
        zIndex: 99999,
      };
    }
  }, [isOverlayFullscreen, isDocumentFullscreen, isInAnyGameScreen, buttonPosition, isDragging]);

  const [portalEl] = useState(() => {
    const el = document.createElement("div");
    el.setAttribute("id", "multi-game-portal");
    return el;
  });

  useEffect(() => {
    if (!document.body.contains(portalEl)) {
      document.body.appendChild(portalEl);
    }
    return () => {
      if (document.body.contains(portalEl)) document.body.removeChild(portalEl);
    };
  }, [portalEl]);

  if (authStatus !== "authenticated") {
    return null;
  }

  if (topSixGames.length === 0) {
    return null;
  }

  if (location.pathname.startsWith("/lotoven")) {
    return null;
  }

  const buttonElement = !isMiniModalOpen && !isModalOpen && !isGamanzaDrawerOpen && (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleButtonClick}
      onPointerDown={handlePointerDown}
      aria-label={t("multiGameFloatingButton")}
      data-testid="multi-game-floating-button"
      id={"button-multi-game"}
      className={`${styles.multiGameButton} ${showGamanzaAvatar ? styles.multiGameButtonAvatar : ""} ${
        isDocumentFullscreen ? "multi-game-button-fullscreen" : ""
      } ${isDragging ? styles.dragging : ""}`}
      style={buttonStyle}
      onDoubleClick={() => {
        // Double-click to exit fullscreen when in fullscreen mode
        if (isDocumentFullscreen) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
        }
      }}
    >
      {showGamanzaAvatar ? (
        <div
          ref={avatarHostRef}
          className={styles.avatarHost}
          aria-hidden
          aria-busy={isAvatarHydrating}
        >
          {isAvatarHydrating && <span className={styles.avatarSkeleton} />}
          <div
            id={FAB_AVATAR_WIDGET_ID}
            className={`gamification_widget ${styles.avatarWidget} ${
              isAvatarHydrating ? styles.avatarPending : styles.avatarReady
            }`}
            data-type="avatar"
            data-size={String(GAMANZA_AVATAR_DATA_SIZE)}
          />
        </div>
      ) : (
        <IconBoltFilled
          size={24}
          className={styles.buttonIcon}
        />
      )}
    </button>
  );

  return (
    <>
      {buttonElement && createPortal(buttonElement, portalEl)}

      <MultiGameModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onMinimize={handleMinimizeModal}
        games={topSixGames}
        onGameSelect={handleGameSelect}
      />

      <MiniGameModal
        isOpen={isMiniModalOpen}
        onClose={handleCloseMiniModal}
        onMinimize={handleOpenFullModal}
        games={topSixGames}
        onGameSelect={handleGameSelect}
      />

      {parallelGame && (
        <ParallelGameModal
          game={parallelGame}
          onClose={handleCloseParallelGame}
          onMinimize={handleMinimizeParallelGame}
          isMinimized={isParallelGameMinimized}
        />
      )}
    </>
  );
};

export default MultiGameFloatingButton;
